// WhatsApp — paylaşılan WhatsApp/Telegram görüşme dosyasının (marka başına bir sekme)
// istatistikleri (İstatistik) + kayıtları (Kayıtlar). Veri /api/whatsapp'tan: sunucu her istekte
// sheet'i okur (2 dk cache), Postgres'te hiçbir şey yok. Marka kapsamı sunucuda uygulanır —
// süpervizör yalnız kendi markalarının sekmelerini görür (sahibin isteği 2026-09-20, görev
// #104/#105: "spv & admin should see whatsapp data in another tab").
//
// Sonuç/sebep/kanal etiketleri sheet'in BİREBİR metnidir (haftalık raporla aynı kural — katlama
// uydurulmaz); renk için motor kanonu `resultKey` durum sisteminden çözülür.

import { useEffect, useMemo, useState } from 'react';
import { getStatusSystems, getWhatsappReport } from '../api';
import { brandLabel, tzOfBrands } from '../constants';
import { computePreset, downloadCSV, fmtDate } from '../format';
import type { BrandId, Lang, ReportPreset, WhatsappReport, WhatsappSourceStatus } from '../types';
import { cardStyle, CsvButton, dotStyle, EmptyState, Icon, Select, segStyle, Skeleton } from '../ui';

const PRESETS: [ReportPreset, string][] = [
  ['today', 'Bugün'],
  ['yesterday', 'Dün'],
  ['this_week', 'Bu Hafta'],
  ['last_week', 'Geçen Hafta'],
  ['this_month', 'Bu Ay'],
];

const dateFilterStyle = {
  background: 'var(--inset)',
  border: '1px solid var(--border)',
  borderRadius: '9px',
  padding: '8px 10px',
  color: 'var(--text)',
  fontSize: '12.5px',
  outline: 'none',
  colorScheme: 'dark',
} as const;

type View = 'stats' | 'log';

// SVG donut için: r=54 → çevre ≈ 339.29. Her dilim stroke-dasharray + kümülatif offset ile çizilir.
const DONUT_R = 54;
const DONUT_C = 2 * Math.PI * DONUT_R;

// Kanal renkleri — iki bilinen kanal + diğerleri gri (yeni renk uydurma: App.tsx jeton paleti).
const CHANNEL_COLORS: Record<string, string> = { WHATSAPP: '#37c46b', TELEGRAM: '#3d8bff' };
const channelColor = (c: string) => CHANNEL_COLORS[c.toUpperCase()] || 'var(--dim)';

// Sunucunun marka başına kaynak durumu → kullanıcıya açıklama (ok = not basılmaz).
const SOURCE_NOTE: Record<Exclude<WhatsappSourceStatus, 'ok'>, string> = {
  missing: 'WhatsApp dosyasında bu markanın sekmesi yok',
  unconfigured: 'WhatsApp dosyası sunucuda yapılandırılmamış',
  quota: 'Google Sheets kotası doldu — birazdan tekrar deneyin',
  shifted: 'Dosya okuma sırasında değişti — tekrar deneyin',
  error: 'WhatsApp dosyası okunamadı',
};

function KpiTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ ...cardStyle, padding: '14px 16px', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '10.5px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function BarList({ rows, color, labelWidth = 150 }: { rows: { label: string; count: number }[]; color: string; labelWidth?: number }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto' }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{ width: labelWidth + 'px', flex: 'none', fontSize: '11.5px', color: 'var(--dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={r.label}
          >
            {r.label}
          </span>
          <div style={{ flex: 1, height: '12px', background: 'var(--inset)', borderRadius: '5px', overflow: 'hidden' }}>
            <div style={{ width: (r.count / max) * 100 + '%', height: '100%', background: color, borderRadius: '5px' }} />
          </div>
          <span style={{ width: '40px', flex: 'none', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
            {r.count}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Whatsapp({
  brands,
}: {
  brands: BrandId[];
  // `lang` App'ten geçer (kabuk tutarlılığı) ama bu sayfa EN kaplamasını i18n DOM katmanından alır.
  lang: Lang;
}) {
  const brandTz = tzOfBrands(brands);
  const [view, setView] = useState<View>('stats');
  const [preset, setPreset] = useState<ReportPreset>('this_week');
  const [start, setStart] = useState(() => computePreset('this_week', brandTz).start);
  const [end, setEnd] = useState(() => computePreset('this_week', brandTz).end);
  // Süzgeçler yalnız Kayıtlar görünümünde ve İSTEMCİDE uygulanır (sunucu tüm kümeyi verir).
  const [agent, setAgent] = useState('');
  const [result, setResult] = useState('');
  const [channel, setChannel] = useState('');
  const [data, setData] = useState<WhatsappReport | null>(null);
  const [loading, setLoading] = useState(true);

  const brandKey = brands.join(',');
  const statusColor = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of getStatusSystems().tr) m[d.key] = d.color;
    return (key: string) => m[key] || '#8b8f98';
  }, []);

  useEffect(() => {
    const range = preset === 'custom' ? { start, end } : computePreset(preset, brandTz);
    if (!range.start || !range.end) return;
    let alive = true;
    setLoading(true);
    getWhatsappReport(brands, {
      preset: preset === 'custom' ? undefined : preset,
      start: preset === 'custom' ? start : undefined,
      end: preset === 'custom' ? end : undefined,
    }).then(
      (d) => {
        if (!alive) return;
        setData(d);
        setLoading(false);
      },
      () => {
        if (!alive) return;
        setData(null);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, start, end, brandKey]);

  const onPreset = (p: ReportPreset) => {
    const rng = computePreset(p, brandTz);
    setPreset(p);
    setStart(rng.start);
    setEnd(rng.end);
  };
  const onStart = (v: string) => {
    setPreset('custom');
    setStart(v);
  };
  const onEnd = (v: string) => {
    setPreset('custom');
    setEnd(v);
  };

  const range = preset === 'custom' ? { start, end } : computePreset(preset, brandTz);
  const rangeLabel = fmtDate(range.start) + ' – ' + fmtDate(range.end);

  const stats = data?.stats;

  // Donut dilimleri: sonuç başına görüşme (renk durum sisteminden).
  const donut = useMemo(() => {
    const items = (stats?.byResult || []).filter((t) => t.count > 0);
    const sum = items.reduce((n, t) => n + t.count, 0);
    let acc = 0;
    const slices = items.map((t) => {
      const len = (sum ? t.count / sum : 0) * DONUT_C;
      const slice = { result: t.result, key: String(t.key), count: t.count, len, offset: -acc };
      acc += len;
      return slice;
    });
    return { slices, sum };
  }, [stats]);

  // Kayıtlar — istemci tarafı süzgeç (seçenekler yüklenen kümeden türetilir).
  const logs = useMemo(() => {
    const all = data?.logs || [];
    return all.filter((r) => (!agent || r.agent === agent) && (!result || r.result === result) && (!channel || r.channel === channel));
  }, [data, agent, result, channel]);
  const options = useMemo(() => {
    const uniq = (xs: string[]) => [...new Set(xs.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr'));
    const all = data?.logs || [];
    return {
      agents: uniq(all.map((r) => r.agent)),
      results: uniq(all.map((r) => r.result)),
      channels: uniq(all.map((r) => r.channel)),
    };
  }, [data]);

  const onCsv = () => {
    const head = ['Tarih', 'Temsilci', 'Marka', 'Kanal', 'Musteri ID', 'Sonuc', 'Sebep', 'Not'];
    downloadCSV(
      'whatsapp-kayitlari.csv',
      [head as (string | number)[]].concat(
        logs.map((r) => [fmtDate(r.day), r.agent, brandLabel(r.brand), r.channel, r.customerId, r.result, r.reason, r.note]),
      ),
    );
  };

  const loadingNow = loading && !data;
  const sourceNotes = (data?.sources || []).filter((s) => s.status !== 'ok');
  const emptyReason =
    brands.length === 0
      ? 'Üstteki marka filtresinden en az bir marka seçin.'
      : sourceNotes.length && !data?.stats.total
        ? sourceNotes.map((s) => brandLabel(s.brand) + ': ' + SOURCE_NOTE[s.status as Exclude<WhatsappSourceStatus, 'ok'>]).join(' · ')
        : 'Seçili dönem ve markalar için WhatsApp kaydı bulunamadı.';
  const statsEmpty = !!stats && stats.total === 0;
  const logsEmpty = !!data && logs.length === 0;

  const logCols = '88px 140px 90px 100px 130px 150px 150px 1fr';

  return (
    <>
      {/* görünüm + dönem seçimi */}
      <div style={{ ...cardStyle, padding: '16px 18px', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <button onClick={() => setView('stats')} style={segStyle(view === 'stats')}>İstatistik</button>
          <button onClick={() => setView('log')} style={segStyle(view === 'log')}>Kayıtlar</button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--dim)', fontSize: '12.5px' }}>
            <Icon name="event" size={17} color="var(--faint)" />
            <span>{rangeLabel}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.06em', color: 'var(--faint)', marginRight: '2px' }}>DÖNEM</span>
          {PRESETS.map(([k, l]) => (
            <button key={k} onClick={() => onPreset(k)} style={segStyle(preset === k)}>
              {l}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
            <input type="date" value={start} onChange={(e) => onStart(e.target.value)} style={dateFilterStyle} />
            <span style={{ color: 'var(--faint)' }}>–</span>
            <input type="date" value={end} onChange={(e) => onEnd(e.target.value)} style={dateFilterStyle} />
          </div>
        </div>
      </div>

      {loadingNow && <Skeleton />}

      {/* kaynak notu — bir marka veri veremediyse sıfır sessizce kalmaz */}
      {!loadingNow && data && sourceNotes.length > 0 && data.stats.total > 0 && (
        <div style={{ ...cardStyle, padding: '10px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#e0a23c' }}>
          <Icon name="warning" size={17} color="#e0a23c" />
          <span>{sourceNotes.map((s) => brandLabel(s.brand) + ': ' + SOURCE_NOTE[s.status as Exclude<WhatsappSourceStatus, 'ok'>]).join(' · ')}</span>
        </div>
      )}

      {/* ── İSTATİSTİK ── */}
      {!loadingNow && view === 'stats' && stats && !statsEmpty && (
        <>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
            <KpiTile label="TOPLAM GÖRÜŞME" value={String(stats.total)} />
            <KpiTile label="YANITLANAN" value={String(stats.answered)} color="#37c46b" />
            <KpiTile label="YANIT ORANI" value={'%' + stats.answerRatePct} color="#3d8bff" />
            <KpiTile label="TEMSİLCİ" value={String(stats.byAgent.length)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: '14px', marginBottom: '18px' }}>
            {/* sonuç dağılımı (donut) + kanal — tek kart, iki yarım */}
            <div style={{ ...cardStyle, padding: '16px 18px', display: 'flex', flexDirection: 'column' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Sonuç dağılımı</div>
                <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '14px' }}>Görüşme sonucuna göre</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
                  <svg viewBox="0 0 140 140" style={{ width: '140px', height: '140px', flex: 'none', transform: 'rotate(-90deg)' }}>
                    <circle cx="70" cy="70" r={DONUT_R} fill="none" style={{ stroke: 'var(--inset)' }} strokeWidth="16" />
                    {donut.slices.map((s) => (
                      <circle
                        key={s.result}
                        cx="70"
                        cy="70"
                        r={DONUT_R}
                        fill="none"
                        stroke={statusColor(s.key)}
                        strokeWidth="16"
                        strokeDasharray={`${s.len} ${DONUT_C - s.len}`}
                        strokeDashoffset={s.offset}
                      />
                    ))}
                  </svg>
                  <div style={{ flex: 1, minWidth: '150px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {stats.byResult.map((t) => (
                      <div key={t.result} style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                        <span style={dotStyle(statusColor(String(t.key)), 9)} />
                        <span style={{ flex: 1, fontSize: '12px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.result}
                        </span>
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{t.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', margin: '16px -18px' }} />

              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Kanal dağılımı</div>
                <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '14px' }}>WhatsApp / Telegram</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {stats.byChannel.map((c) => (
                    <div key={c.channel} style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <span style={dotStyle(channelColor(c.channel), 9)} />
                      <span style={{ flex: 1, fontSize: '12px', color: 'var(--text)' }}>{c.channel}</span>
                      <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{c.count}</span>
                      <span style={{ width: '48px', textAlign: 'right', fontSize: '11px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                        {'%' + Math.round((100 * c.count) / Math.max(1, stats.total))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* görüşme sebepleri */}
            <div style={{ ...cardStyle, padding: '16px 18px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Görüşme sebepleri</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '14px' }}>Sheet'teki sebep sütunu, birebir</div>
              <BarList rows={stats.byReason.map((r) => ({ label: r.reason, count: r.count }))} color="#3d8bff" />
            </div>

            {/* temsilci başına */}
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                <Icon name="groups" size={19} color="var(--accent)" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Temsilci başına</div>
                </div>
                <span style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{stats.byAgent.length + ' temsilci'}</span>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 92px 92px 84px',
                  gap: '10px',
                  padding: '9px 18px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.04em',
                  color: 'var(--faint)',
                }}
              >
                <span>TEMSİLCİ</span>
                <span style={{ textAlign: 'right' }}>GÖRÜŞME</span>
                <span style={{ textAlign: 'right' }}>YANITLANAN</span>
                <span style={{ textAlign: 'right' }}>ORAN</span>
              </div>
              <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
                {stats.byAgent.map((a) => (
                  <div
                    key={a.name}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 92px 92px 84px',
                      gap: '10px',
                      padding: '11px 18px',
                      borderBottom: '1px solid var(--border)',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.name}
                    </span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{a.total}</span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: '#37c46b', fontVariantNumeric: 'tabular-nums' }}>{a.answered}</span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{'%' + a.answerRatePct}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* günlere göre */}
            <div style={{ ...cardStyle, padding: '16px 18px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Günlere göre</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '14px' }}>Gün başına görüşme sayısı</div>
              <BarList rows={stats.byDay.map((d) => ({ label: fmtDate(d.day), count: d.count }))} color="#37c46b" labelWidth={90} />
            </div>
          </div>
        </>
      )}

      {!loadingNow && view === 'stats' && (statsEmpty || !stats) && (
        <EmptyState icon="chat" title="WhatsApp kaydı yok" reason={emptyReason} />
      )}

      {/* Kayıtlar görünümü, veri gelmediyse (ağ hatası) de boş durum gösterir — beyaz ekran bırakmaz. */}
      {!loadingNow && view === 'log' && !data && <EmptyState icon="chat" title="WhatsApp kaydı yok" reason={emptyReason} />}

      {/* ── KAYITLAR ── */}
      {!loadingNow && view === 'log' && data && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <Select
              compact
              value={agent}
              onChange={setAgent}
              options={[{ value: '', label: 'Tüm temsilciler' }].concat(options.agents.map((a) => ({ value: a, label: a })))}
            />
            <Select
              compact
              value={result}
              onChange={setResult}
              options={[{ value: '', label: 'Tüm sonuçlar' }].concat(options.results.map((r) => ({ value: r, label: r })))}
            />
            <Select
              compact
              value={channel}
              onChange={setChannel}
              options={[{ value: '', label: 'Tüm kanallar' }].concat(options.channels.map((c) => ({ value: c, label: c })))}
            />
            <div style={{ marginLeft: 'auto' }}>
              <CsvButton onClick={onCsv} />
            </div>
          </div>

          {logsEmpty ? (
            <EmptyState icon="chat" title="WhatsApp kaydı yok" reason={emptyReason} />
          ) : (
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
                <Icon name="chat" size={19} color="var(--accent)" />
                <div style={{ flex: 1, fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Görüşme kayıtları</div>
                <span style={{ fontSize: '12px', color: 'var(--dim)' }}>
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{logs.length}</span> kayıt
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: '1000px' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: logCols,
                      gap: '10px',
                      padding: '9px 18px',
                      borderBottom: '1px solid var(--border)',
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '.04em',
                      color: 'var(--faint)',
                    }}
                  >
                    <span>TARİH</span>
                    <span>TEMSİLCİ</span>
                    <span>MARKA</span>
                    <span>KANAL</span>
                    <span>MÜŞTERİ ID</span>
                    <span>SONUÇ</span>
                    <span>SEBEP</span>
                    <span>NOT</span>
                  </div>
                  <div style={{ maxHeight: '560px', overflowY: 'auto' }}>
                    {logs.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: logCols,
                          gap: '10px',
                          padding: '10px 18px',
                          borderBottom: '1px solid var(--border)',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{fmtDate(r.day)}</span>
                        <span style={{ fontSize: '12px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.agent}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--dim)' }}>{brandLabel(r.brand)}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text)', minWidth: 0 }}>
                          <span style={dotStyle(channelColor(r.channel))} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.channel}</span>
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.customerId}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: statusColor(String(r.resultKey)), minWidth: 0 }}>
                          <span style={dotStyle(statusColor(String(r.resultKey)))} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.result}</span>
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.reason}>
                          {r.reason}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.note}>
                          {r.note}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {data.logTruncated && (
                <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', fontSize: '11.5px', color: 'var(--faint)' }}>
                  {'En yeni ' + data.logs.length + ' kayıt gösteriliyor — daraltmak için dönem veya süzgeç kullanın'}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
